import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '../api';

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [job, setJob] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCOForm, setShowCOForm] = useState(false);
  const [showPayForm, setShowPayForm] = useState(false);
  const [showMatForm, setShowMatForm] = useState(false);
  const [showLabForm, setShowLabForm] = useState(false);
  const [coForm, setCoForm] = useState({ co_date: '', description: '', amount: '', signed: false, notes: '' });
  const [payForm, setPayForm] = useState({ payment_date: '', description: '', amount: '', method: '', notes: '' });
  const [matForm, setMatForm] = useState({ material_date: '', description: '', vendor: '', category: '', quantity: '', unit_cost: '' });
  const [labForm, setLabForm] = useState({ labor_date: '', employee: '', description: '', pay_type: 'hourly', hours: '', rate: '', amount: '' });
  const [settings, setSettings] = useState({ company_name: 'JobCostNow', logo_url: '' });

  useEffect(() => { loadJob(); loadSettings(); }, [id]);

  const loadJob = async () => {
    const res = await axios.get(`${API_URL}/api/jobs/${id}`, { headers });
    setJob(res.data);
  };

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      if (res.data && res.data.company_name) {
        setSettings({ company_name: res.data.company_name || 'JobCostNow', logo_url: res.data.logo_url || '' });
      }
    } catch (err) {}
  };

  const fmt = (n) => '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  const deleteJob = async () => {
    if (!window.confirm(`Delete the job for "${job.customer_name}"? This permanently removes the job and all its change orders, payments, materials, and labor. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/jobs/${id}`, { headers });
      navigate('/');
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const addCO = async (e) => {
    e.preventDefault();
    await axios.post(`${API_URL}/api/entries/jobs/${id}/change-orders`, coForm, { headers });
    setCoForm({ co_date: '', description: '', amount: '', signed: false, notes: '' });
    setShowCOForm(false); loadJob();
  };

  const addPayment = async (e) => {
    e.preventDefault();
    await axios.post(`${API_URL}/api/entries/jobs/${id}/payments`, payForm, { headers });
    setPayForm({ payment_date: '', description: '', amount: '', method: '', notes: '' });
    setShowPayForm(false); loadJob();
  };

  const addMaterial = async (e) => {
    e.preventDefault();
    await axios.post(`${API_URL}/api/entries/jobs/${id}/materials`, matForm, { headers });
    setMatForm({ material_date: '', description: '', vendor: '', category: '', quantity: '', unit_cost: '' });
    setShowMatForm(false); loadJob();
  };

  const addLabor = async (e) => {
    e.preventDefault();
    await axios.post(`${API_URL}/api/entries/jobs/${id}/labor`, labForm, { headers });
    setLabForm({ labor_date: '', employee: '', description: '', pay_type: 'hourly', hours: '', rate: '', amount: '' });
    setShowLabForm(false); loadJob();
  };

  if (!job) return <div style={{textAlign:'center',padding:'60px'}}>Loading...</div>;

  const totalCOs = job.change_orders?.reduce((s, co) => s + parseFloat(co.amount), 0) || 0;
  const totalPaid = job.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0;
  const totalMaterials = job.materials?.reduce((s, m) => s + parseFloat(m.total_cost), 0) || 0;
  const totalLabor = job.labor?.reduce((s, l) => s + parseFloat(l.total), 0) || 0;
  const revisedContract = parseFloat(job.contract_amount) + totalCOs;
  const balanceDue = revisedContract - totalPaid;
  const totalCosts = totalMaterials + totalLabor;
  const grossMargin = revisedContract > 0 ? ((revisedContract - totalCosts) / revisedContract * 100).toFixed(1) : 0;
  const portalUrl = `${window.location.origin}/portal/${job.portal_token}`;

  const payTypeLabel = (t) => t === 'piece' ? 'Piece Rate' : t === 'commission' ? 'Commission' : 'Hourly';

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => navigate('/')} style={styles.back}>← Dashboard</button>
          {settings.logo_url
            ? <img src={settings.logo_url} alt={settings.company_name} style={{maxHeight:'34px',maxWidth:'140px',objectFit:'contain'}} onError={e => { e.target.style.display='none'; }} />
            : null}
          <div>
            <h1 style={styles.jobName}>{job.customer_name}</h1>
            <span style={styles.jobSub}>{job.job_address} {job.job_number ? `· Job #${job.job_number}` : ''}</span>
          </div>
        </div>
        <div style={{display:'flex',gap:'10px'}}>
          <button onClick={() => navigate(`/jobs/${id}/report`)} style={styles.btnReport}>📄 View Report</button>
          <button onClick={() => {navigator.clipboard.writeText(portalUrl); alert('Portal link copied!');}} style={styles.btnSecondary}>📋 Copy Portal Link</button>
          <button onClick={deleteJob} style={styles.btnDelete}>🗑 Delete Job</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCard}><div style={styles.kpiLabel}>Original Contract</div><div style={styles.kpiValue}>{fmt(job.contract_amount)}</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiLabel}>Revised Contract</div><div style={styles.kpiValue}>{fmt(revisedContract)}</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiLabel}>Total Collected</div><div style={{...styles.kpiValue, color:'#1E7145'}}>{fmt(totalPaid)}</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiLabel}>Balance Due</div><div style={{...styles.kpiValue, color:'#C0392B'}}>{fmt(balanceDue)}</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiLabel}>Total Costs</div><div style={styles.kpiValue}>{fmt(totalCosts)}</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiLabel}>Gross Margin</div><div style={{...styles.kpiValue, color: parseFloat(grossMargin) > 20 ? '#1E7145' : '#C0392B'}}>{grossMargin}%</div></div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {['overview','change-orders','payments','materials','labor'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{...styles.tab, ...(activeTab===tab ? styles.tabActive : {})}}>{tab.replace('-',' ').replace(/\b\w/g,c=>c.toUpperCase())}</button>
        ))}
      </div>

      <div style={styles.content}>
        {/* CHANGE ORDERS */}
        {activeTab === 'change-orders' && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Change Orders</h3>
              <button onClick={() => setShowCOForm(!showCOForm)} style={styles.btnPrimary}>+ Add CO</button>
            </div>
            {showCOForm && (
              <form onSubmit={addCO} style={styles.formGrid}>
                <input style={styles.input} type="date" value={coForm.co_date} onChange={e => setCoForm({...coForm, co_date: e.target.value})} />
                <input style={styles.input} placeholder="Description *" value={coForm.description} onChange={e => setCoForm({...coForm, description: e.target.value})} required />
                <input style={styles.input} type="number" placeholder="Amount *" value={coForm.amount} onChange={e => setCoForm({...coForm, amount: e.target.value})} required />
                <input style={styles.input} placeholder="Notes" value={coForm.notes} onChange={e => setCoForm({...coForm, notes: e.target.value})} />
                <label style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'14px'}}><input type="checkbox" checked={coForm.signed} onChange={e => setCoForm({...coForm, signed: e.target.checked})} /> Signed</label>
                <button style={styles.btnPrimary} type="submit">Save</button>
              </form>
            )}
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.th}>Amount</th><th style={styles.th}>Status</th><th style={styles.th}>Notes</th></tr></thead>
              <tbody>{job.change_orders?.map((co, i) => (
                <tr key={co.id} style={{background:i%2===0?'#fff':'#f9f9f9'}}>
                  <td style={styles.td}>{co.co_date ? new Date(co.co_date).toLocaleDateString() : '—'}</td>
                  <td style={styles.td}>{co.description}</td>
                  <td style={styles.td}>{fmt(co.amount)}</td>
                  <td style={styles.td}><span style={{color:co.signed?'#1E7145':'#E67E22',fontWeight:'bold'}}>{co.signed?'Signed':'Verbal Only'}</span></td>
                  <td style={styles.td}>{co.notes || '—'}</td>
                </tr>
              ))}</tbody>
            </table>
            <div style={styles.totalRow}>Total Change Orders: <strong>{fmt(totalCOs)}</strong></div>
          </div>
        )}

        {/* PAYMENTS */}
        {activeTab === 'payments' && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Payments</h3>
              <button onClick={() => setShowPayForm(!showPayForm)} style={styles.btnPrimary}>+ Add Payment</button>
            </div>
            {showPayForm && (
              <form onSubmit={addPayment} style={styles.formGrid}>
                <input style={styles.input} type="date" value={payForm.payment_date} onChange={e => setPayForm({...payForm, payment_date: e.target.value})} />
                <input style={styles.input} placeholder="Description" value={payForm.description} onChange={e => setPayForm({...payForm, description: e.target.value})} />
                <input style={styles.input} type="number" placeholder="Amount *" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: e.target.value})} required />
                <input style={styles.input} placeholder="Method (check, ACH...)" value={payForm.method} onChange={e => setPayForm({...payForm, method: e.target.value})} />
                <input style={styles.input} placeholder="Notes" value={payForm.notes} onChange={e => setPayForm({...payForm, notes: e.target.value})} />
                <button style={styles.btnPrimary} type="submit">Save</button>
              </form>
            )}
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.th}>Method</th><th style={styles.th}>Amount</th><th style={styles.th}>Notes</th></tr></thead>
              <tbody>{job.payments?.map((p, i) => (
                <tr key={p.id} style={{background:i%2===0?'#fff':'#f9f9f9'}}>
                  <td style={styles.td}>{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '—'}</td>
                  <td style={styles.td}>{p.description || '—'}</td>
                  <td style={styles.td}>{p.method || '—'}</td>
                  <td style={{...styles.td,color:'#1E7145',fontWeight:'bold'}}>{fmt(p.amount)}</td>
                  <td style={styles.td}>{p.notes || '—'}</td>
                </tr>
              ))}</tbody>
            </table>
            <div style={styles.totalRow}>Total Paid: <strong style={{color:'#1E7145'}}>{fmt(totalPaid)}</strong> &nbsp;|&nbsp; Balance Due: <strong style={{color:'#C0392B'}}>{fmt(balanceDue)}</strong></div>
          </div>
        )}

        {/* MATERIALS */}
        {activeTab === 'materials' && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Materials</h3>
              <button onClick={() => setShowMatForm(!showMatForm)} style={styles.btnPrimary}>+ Add Material</button>
            </div>
            {showMatForm && (
              <form onSubmit={addMaterial} style={styles.formGrid}>
                <input style={styles.input} type="date" value={matForm.material_date} onChange={e => setMatForm({...matForm, material_date: e.target.value})} />
                <input style={styles.input} placeholder="Description *" value={matForm.description} onChange={e => setMatForm({...matForm, description: e.target.value})} required />
                <input style={styles.input} placeholder="Vendor" value={matForm.vendor} onChange={e => setMatForm({...matForm, vendor: e.target.value})} />
                <input style={styles.input} placeholder="Category" value={matForm.category} onChange={e => setMatForm({...matForm, category: e.target.value})} />
                <input style={styles.input} type="number" placeholder="Qty *" value={matForm.quantity} onChange={e => setMatForm({...matForm, quantity: e.target.value})} required />
                <input style={styles.input} type="number" placeholder="Unit Cost *" value={matForm.unit_cost} onChange={e => setMatForm({...matForm, unit_cost: e.target.value})} required />
                <button style={styles.btnPrimary} type="submit">Save</button>
              </form>
            )}
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Description</th><th style={styles.th}>Vendor</th><th style={styles.th}>Qty</th><th style={styles.th}>Unit Cost</th><th style={styles.th}>Total</th></tr></thead>
              <tbody>{job.materials?.map((m, i) => (
                <tr key={m.id} style={{background:i%2===0?'#fff':'#f9f9f9'}}>
                  <td style={styles.td}>{m.material_date ? new Date(m.material_date).toLocaleDateString() : '—'}</td>
                  <td style={styles.td}>{m.description}</td>
                  <td style={styles.td}>{m.vendor || '—'}</td>
                  <td style={styles.td}>{m.quantity}</td>
                  <td style={styles.td}>{fmt(m.unit_cost)}</td>
                  <td style={styles.td}>{fmt(m.total_cost)}</td>
                </tr>
              ))}</tbody>
            </table>
            <div style={styles.totalRow}>Total Materials: <strong>{fmt(totalMaterials)}</strong></div>
          </div>
        )}

        {/* LABOR */}
        {activeTab === 'labor' && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Labor</h3>
              <button onClick={() => setShowLabForm(!showLabForm)} style={styles.btnPrimary}>+ Add Labor</button>
            </div>
            {showLabForm && (
              <form onSubmit={addLabor} style={styles.formGrid}>
                <input style={styles.input} type="date" value={labForm.labor_date} onChange={e => setLabForm({...labForm, labor_date: e.target.value})} />
                <input style={styles.input} placeholder="Employee *" value={labForm.employee} onChange={e => setLabForm({...labForm, employee: e.target.value})} required />
                <select style={styles.input} value={labForm.pay_type} onChange={e => setLabForm({...labForm, pay_type: e.target.value})}>
                  <option value="hourly">Hourly</option>
                  <option value="piece">Piece Rate</option>
                  <option value="commission">Commission</option>
                </select>
                <input style={styles.input} placeholder="Task Description" value={labForm.description} onChange={e => setLabForm({...labForm, description: e.target.value})} />
                {labForm.pay_type === 'hourly' ? (
                  <>
                    <input style={styles.input} type="number" placeholder="Hours *" value={labForm.hours} onChange={e => setLabForm({...labForm, hours: e.target.value})} required />
                    <input style={styles.input} type="number" placeholder="Rate ($/hr) *" value={labForm.rate} onChange={e => setLabForm({...labForm, rate: e.target.value})} required />
                  </>
                ) : (
                  <input style={styles.input} type="number" placeholder={labForm.pay_type === 'commission' ? 'Commission ($) *' : 'Amount ($) *'} value={labForm.amount} onChange={e => setLabForm({...labForm, amount: e.target.value})} required />
                )}
                <button style={styles.btnPrimary} type="submit">Save</button>
              </form>
            )}
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Employee</th><th style={styles.th}>Type</th><th style={styles.th}>Description</th><th style={styles.th}>Hours</th><th style={styles.th}>Rate</th><th style={styles.th}>Total</th></tr></thead>
              <tbody>{job.labor?.map((l, i) => (
                <tr key={l.id} style={{background:i%2===0?'#fff':'#f9f9f9'}}>
                  <td style={styles.td}>{l.labor_date ? new Date(l.labor_date).toLocaleDateString() : '—'}</td>
                  <td style={styles.td}>{l.employee}</td>
                  <td style={styles.td}>{payTypeLabel(l.pay_type)}</td>
                  <td style={styles.td}>{l.description || '—'}</td>
                  <td style={styles.td}>{l.pay_type === 'hourly' ? l.hours : '—'}</td>
                  <td style={styles.td}>{l.pay_type === 'hourly' ? fmt(l.rate) + '/hr' : '—'}</td>
                  <td style={styles.td}>{fmt(l.total)}</td>
                </tr>
              ))}</tbody>
            </table>
            <div style={styles.totalRow}>Total Labor: <strong>{fmt(totalLabor)}</strong></div>
          </div>
        )}

        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Job Cost Summary</h3>
            <table style={styles.table}>
              <tbody>
                <tr><td style={styles.td}>Original Contract</td><td style={{...styles.td,textAlign:'right'}}>{fmt(job.contract_amount)}</td></tr>
                <tr><td style={styles.td}>Change Orders</td><td style={{...styles.td,textAlign:'right'}}>{fmt(totalCOs)}</td></tr>
                <tr style={{background:'#f0f4f8'}}><td style={{...styles.td,fontWeight:'bold'}}>Revised Contract</td><td style={{...styles.td,textAlign:'right',fontWeight:'bold'}}>{fmt(revisedContract)}</td></tr>
                <tr><td style={styles.td}>Materials Cost</td><td style={{...styles.td,textAlign:'right'}}>{fmt(totalMaterials)}</td></tr>
                <tr><td style={styles.td}>Labor Cost</td><td style={{...styles.td,textAlign:'right'}}>{fmt(totalLabor)}</td></tr>
                <tr style={{background:'#f0f4f8'}}><td style={{...styles.td,fontWeight:'bold'}}>Total Costs</td><td style={{...styles.td,textAlign:'right',fontWeight:'bold'}}>{fmt(totalCosts)}</td></tr>
                <tr style={{background:'#E8F5E9'}}><td style={{...styles.td,fontWeight:'bold',color:'#1E7145'}}>Gross Profit</td><td style={{...styles.td,textAlign:'right',fontWeight:'bold',color:'#1E7145'}}>{fmt(revisedContract - totalCosts)}</td></tr>
                <tr style={{background:'#E8F5E9'}}><td style={{...styles.td,color:'#1E7145'}}>Gross Margin</td><td style={{...styles.td,textAlign:'right',color:'#1E7145'}}>{grossMargin}%</td></tr>
                <tr><td style={styles.td}>Total Paid</td><td style={{...styles.td,textAlign:'right',color:'#1E7145'}}>{fmt(totalPaid)}</td></tr>
                <tr style={{background:'#FDEDEC'}}><td style={{...styles.td,fontWeight:'bold',color:'#C0392B'}}>Balance Due</td><td style={{...styles.td,textAlign:'right',fontWeight:'bold',color:'#C0392B'}}>{fmt(balanceDue)}</td></tr>
              </tbody>
            </table>
            <div style={{marginTop:'16px',padding:'12px',background:'#f9f9f9',borderRadius:'6px'}}>
              <strong style={{fontSize:'13px',color:'#666'}}>Customer Portal Link:</strong>
              <div style={{display:'flex',alignItems:'center',gap:'8px',marginTop:'6px'}}>
                <input readOnly value={portalUrl} style={{flex:1,padding:'8px',border:'1px solid #ddd',borderRadius:'4px',fontSize:'12px',color:'#666'}} />
                <button onClick={() => {navigator.clipboard.writeText(portalUrl); alert('Copied!');}} style={styles.btnPrimary}>Copy</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f4f6f9', fontFamily: 'Arial, sans-serif' },
  header: { background: '#2E4057', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  back: { background: 'transparent', color: '#aaa', border: '1px solid #aaa', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px' },
  jobName: { color: '#fff', margin: 0, fontSize: '20px' },
  jobSub: { color: '#aaa', fontSize: '13px' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: '12px', padding: '20px 32px 0', maxWidth: '850px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  kpiCard: { background: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' },
  kpiLabel: { fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase' },
  kpiValue: { fontSize: '18px', fontWeight: 'bold', color: '#2E4057' },
  tabs: { display: 'flex', gap: '4px', padding: '20px 32px 0', borderBottom: '2px solid #e0e0e0', marginTop: '20px', maxWidth: '850px', marginLeft: 'auto', marginRight: 'auto', width: '100%', boxSizing: 'border-box' },
  tab: { padding: '10px 20px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#888', borderBottom: '2px solid transparent', marginBottom: '-2px' },
  tabActive: { color: '#2E4057', borderBottom: '2px solid #2E4057', fontWeight: 'bold' },
  content: { padding: '24px 32px', maxWidth: '850px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  card: { background: '#fff', borderRadius: '10px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  cardTitle: { margin: 0, color: '#2E4057', fontSize: '18px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '16px', padding: '16px', background: '#f9f9f9', borderRadius: '8px' },
  input: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: '11px', color: '#888', textTransform: 'uppercase', background: '#f9f9f9', letterSpacing: '0.5px' },
  td: { padding: '10px 12px', fontSize: '14px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' },
  totalRow: { padding: '12px', textAlign: 'right', fontSize: '14px', color: '#666', borderTop: '2px solid #f0f0f0', marginTop: '4px' },
  btnPrimary: { padding: '8px 16px', background: '#2E4057', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
  btnSecondary: { padding: '8px 16px', background: '#C9A84C', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
  btnReport: { padding: '8px 16px', background: '#fff', color: '#2E4057', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
  btnDelete: { padding: '8px 16px', background: '#C0392B', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' },
};